import {inject, Injectable} from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError
} from "rxjs";
import {TyrePicture} from "../../core/models/tyrePicture";
import * as Sentry from "@sentry/angular";
import {CustomError} from "../../core/models/error";
import {IndexedDBService} from "./indexed-db.service";
import moment, {duration, Duration, Moment} from "moment";
import {logSentryError, Utils} from "../utils/generics.utils";
import {Picture, PictureStatus, PictureType} from "../../core/models/picture";
import {Analysis, AnalysisType} from "../../core/models/analysis";
import {AppStateService} from "../../core/state/app-state.service";
import {AnalysisCreationService} from "./analysis-creation.service";
import {PhotoshootService} from "../../features/shared/photoshoot/services/photoshoot.service";
import {ExternalApiService} from "../../core/api/external-api/external-api.service";
import {Vehicle} from "../../core/models/vehicle";

@Injectable({
  providedIn: 'root'
})
export class PictureUploadService {

  private appStateService = inject(AppStateService);
  private analysisCreation = inject(AnalysisCreationService);
  private externalApi = inject(ExternalApiService);
  private indexedDB = inject(IndexedDBService);
  private photoshootService = inject(PhotoshootService);

  private readonly obsQueue = new Subject<Observable<Picture>>();
  private readonly onOneUploadFinished$ = new Subject<number>();
  private onAllUploadsFinished$ = new BehaviorSubject(false);
  private cancelUpload$ = new Subject<void>();
  private _failedUploads: Picture[] = [];
  private queueCount = 0;
  private queueCountFinished = 0;
  private currentEstimatedTotalUploadTime: Duration = duration();
  private currentUploadTime: Moment = moment();
  private currentUploadTimesList: number[] = [];
  private uploadQueue: Picture[] = [];

  /** Resolves once the pending pictures stored in IndexedDB have been re-queued (refresh recovery). */
  readonly whenInitialized: Promise<void>;

  constructor() {
    this.observeUploads();
    this.whenInitialized = this.indexedDB.getAllPicturesByStatus(PictureStatus.UPLOADING).then((pictures) => {
      pictures = pictures || [];
      if (pictures.length && this.appStateService.analysis()) {
        Sentry.captureMessage('Started re-upload of pictures', {extra: {pictures: Utils.sanitizePictures(pictures)}});
      }
      pictures.forEach((picture) => {
        this.uploadPicture(picture);
      });
    }).catch((error) => {
      Sentry.captureException(new Error('Unable to re-queue pending pictures'), {extra: {error}});
    });
  }

  private observeUploads() {
    this.obsQueue.pipe(
      tap(() => this.startUploadTimer()),
      mergeMap((obs: Observable<Picture>) => obs.pipe(
        catchError((picture: Picture) => this.onError(picture)),
        tap(() => this.updateUploadTimeEstimate())
      ))).subscribe((picture) => {
        this.onUploadSuccessful(picture);
        this.onUploadCompletion();
      }
    );
  }

  private onError(picture: Picture) {
    this.onUploadError(picture);
    this.onUploadCompletion();
    return EMPTY;
  }

  private startUploadTimer() {
    this.currentUploadTime = moment();
  }

  private updateUploadTimeEstimate() {
    const getSecondHalf = (arr: number[]) => {
      if (arr.length > 2) {
        const middleIndex = Math.floor(arr.length / 2);
        return arr.slice(middleIndex);
      } else {
        return arr;
      }
    }

    this.currentUploadTimesList.push(moment().diff(this.currentUploadTime));
    const secondHalf = getSecondHalf(this.currentUploadTimesList);
    const _seconds =
      ((secondHalf.reduce((sum, num) => sum + num, 0) / secondHalf.length)
        / 1000)
      * (this.uploadQueue.length - 1);
    this.currentEstimatedTotalUploadTime = duration({seconds: Math.floor(_seconds)});
  }

  private onUploadCompletion() {
    this.queueCountFinished++;
    this.onOneUploadFinished$.next(this.queueCountFinished);
    this.uploadQueue.pop();
    if (this.uploadQueue.length !== 0)
      this.handleUpload(this.uploadQueue[this.uploadQueue.length - 1]);
  }

  private onUploadSuccessful(picture: Picture) {
    this.indexedDB.changePictureStatus(picture, PictureStatus.VALID);
    if (picture.type === PictureType.TYRE) {
      this.photoshootService.setPicture(picture as TyrePicture);
    }
  }

  private onUploadError(picture: Picture) {
    picture.status = PictureStatus.ERROR;
    if (picture.type === PictureType.TYRE) {
      this.photoshootService.setPicture(picture as TyrePicture);
    }
    this.failedUploads.push(picture);
  }

  private handleTyrePictureUpload(picture: TyrePicture) {
    if (picture.data) {
      const subject = this.ensureAnalysisId$()
        .pipe(
          mergeMap((analysisId) => {
            const file = picture.toFile()!;
            return this.externalApi.uploadTyrePicture(analysisId, picture.step.type, picture.step.position, file);
          }),
          map(_ => picture as Picture),
          catchError((error) => {
            logSentryError(error, 'Image upload error', {extra: {error, picture: Utils.sanitizePictures(picture)}});
            return throwError(() => picture)
          }),
          takeUntil(this.cancelUpload$)) // Cancel the upload when the cancelUpload$ subject emits
      this.obsQueue.next(subject);
    } else {
      this.obsQueue.next(throwError(() => picture));
    }
  }

  /** Returns the current analysis id, creating the analysis when none exists yet. */
  private ensureAnalysisId$(): Observable<number> {
    const analysisId = this.appStateService.analysis()?.id;
    if (analysisId) {
      return of(analysisId);
    }
    return this.createAnalysis$().pipe(map((analysis) => analysis.id!));
  }

  private createAnalysis$(): Observable<Analysis> {
    if (!this.appStateService.company()?.key) {
      return throwError(() => new Error(CustomError.NO_SUCH_COMPANY));
    }
    return this.analysisCreation.createOrResume$(this.appStateService.snapshot).pipe(
      map((response) => {
        const analysis = Analysis.create(response);
        const storedVehicle = Vehicle.create(response.vehicle.id, response.vehicle.license, response.vehicle.licenseCountry);
        this.appStateService.patch({analysis, vehicle: storedVehicle});
        return analysis;
      }));
  }

  get onAllPicturesUploaded() {
    return this.onAllUploadsFinished$.asObservable();
  }

  get onUpload() {
    return this.onOneUploadFinished$.asObservable();
  }

  isUploadInProgress() {
    return this.queueCountFinished < this.queueCount;
  }

  get uploadCount() {
    return this.queueCount;
  }

  get finishedUploadCount() {
    return this.queueCountFinished;
  }

  reset(endSession: boolean) {
    this.queueCount = 0;
    this.queueCountFinished = 0;
    this.currentUploadTimesList = [];
    this.currentEstimatedTotalUploadTime = duration();
    this.currentUploadTime = moment();
    this.failedUploads = [];
    if (endSession)
      this.onAllUploadsFinished$.next(false);
  }

  hasFailedUploads() {
    return this.failedUploads.length !== 0;
  }

  get failedUploadCount() {
    return this.failedUploads.length;
  }

  get failedUploads() {
    return this._failedUploads;
  }

  get estimatedUploadTime() {
    return this.currentEstimatedTotalUploadTime;
  }

  private set failedUploads(value: Picture[]) {
    this._failedUploads = value;
  }

  allUploadsFinished() {
    this.onAllUploadsFinished$.next(true);
  }

  cancelUpload() {
    this.reset(false);
    this.uploadQueue = [];
    this.cancelUpload$.next();
  }

  uploadPicture(picture: Picture) {
    const samePictureInQueue = !!this.uploadQueue.find((p) => p.key === picture.key);
    if (!samePictureInQueue) {
      if (!picture.data || !picture.data?.byteLength) {
        this.indexedDB.deletePicture(picture.key);
        Sentry.captureException(new Error(CustomError.ABNORMAL_PICTURE_SIZE), {extra: {picture: Utils.sanitizePictures(picture)}});
        return;
      }
      this.indexedDB.addPictureIfNotPresent(picture);
      this.queueCount++;
      if (this.uploadQueue.length === 0) {
        this.handleUpload(picture);
      }
      this.uploadQueue.unshift(picture);
    }
  }

  private handleUpload(picture: Picture) {
    switch (picture.type) {
      case PictureType.VIN:
        this.handleVehicleAnalysisPicture(picture, AnalysisType.VIN);
        break;
      case PictureType.REGISTRATION_CARD:
        this.handleVehicleAnalysisPicture(picture, AnalysisType.REGISTRATION_CARD);
        break;
      default:
        this.handleTyrePictureUpload(picture as TyrePicture);
    }
  }

  /**
   * VIN and registration card pictures are uploaded in a single multipart call
   * (POST /v1/web-app/analyses/{id}/vehicle-analysis-pictures), like tyre pictures.
   */
  private handleVehicleAnalysisPicture(picture: Picture, analysisType: AnalysisType) {
    if (picture.data) {
      const subject = this.createVehicleAnalysis(analysisType, picture)
        .pipe(
          map(_ => picture),
          catchError((error) => {
            logSentryError(error, 'Image upload error', {extra: {error, picture: Utils.sanitizePictures(picture)}});
            return throwError(() => picture)
          }),
          takeUntil(this.cancelUpload$), // Cancel the upload when the cancelUpload$ subject emits
        );
      this.obsQueue.next(subject);
    } else {
      this.obsQueue.next(throwError(() => picture));
    }
  }

  createVehicleAnalysis(analysisType: AnalysisType, picture: Picture) {
    const company = this.appStateService.company();
    if (!company?.key) {
      return throwError(() => new Error(CustomError.NO_SUCH_COMPANY));
    }
    if (!this.appStateService.vehicle()?.id) {
      return throwError(() => new Error(CustomError.NO_SUCH_VEHICLE));
    }
    const analysis = this.appStateService.analysis();
    if (analysis?.id) {
      return this.externalApi.createVehicleAnalysis(analysis.id, analysisType, picture.toFile()!);
    } else {
      return this.createAnalysis$().pipe(
        switchMap((createdAnalysis) => this.externalApi.createVehicleAnalysis(createdAnalysis.id!, analysisType, picture.toFile()!)));
    }
  }
}
